/**
 * PWA Navigation Utilities
 *
 * Uses hash-based routing to avoid iOS Safari PWA issues where path changes
 * trigger the browser UI to appear. With hash routing, the pathname stays
 * constant (/) and only the hash changes, which works reliably on all platforms.
 */

// Cache the standalone check result since it won't change during session
let _isStandaloneCache: boolean | null = null;

/**
 * Check if running in standalone PWA mode (any platform)
 */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;

  if (_isStandaloneCache === null) {
    _isStandaloneCache =
      ('standalone' in window.navigator && (window.navigator as { standalone?: boolean }).standalone === true) ||
      window.matchMedia('(display-mode: standalone)').matches;
  }

  return _isStandaloneCache;
}

/**
 * Check if running as iOS standalone PWA
 */
export function isIOSStandalone(): boolean {
  if (typeof window === 'undefined') return false;

  // Check for iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  return isIOS && isStandalone();
}

/**
 * Push a new hash URL to history.
 * Works on all platforms including iOS PWA because only the hash changes.
 */
export function safePushState(hashUrl: string): void {
  // Ensure the URL is hash-based
  const url = hashUrl.startsWith('#') ? hashUrl : `#${hashUrl}`;
  window.history.pushState(null, '', url);
}

/**
 * Replace current hash URL in history.
 */
export function safeReplaceState(hashUrl: string): void {
  // Ensure the URL is hash-based
  const url = hashUrl.startsWith('#') ? hashUrl : `#${hashUrl}`;
  window.history.replaceState(null, '', url);
}

/**
 * Initialize PWA mode detection.
 * Adds 'pwa-standalone' class to document element when in standalone mode.
 * Call this once on app startup.
 */
export function initIOSPwaNavigation(): () => void {
  // Add standalone class for CSS targeting (since display-mode media query is unreliable on iOS)
  if (isStandalone()) {
    document.documentElement.classList.add('pwa-standalone');
  }

  // No need for click/submit interceptors with hash-based routing
  // since the pathname never changes and hash changes work reliably
  return () => {};
}
